






































































































import { computed, ref, watch, onMounted } from '@nuxtjs/composition-api';
import {
  BaseDivider,
  ProgressStepper,
  TypeBody,
  TypeDisplay,
  TypeHeading,
  BaseIcon,
} from '@bambeehr/pollen';
import PaymentProcessingLoader from '@/modules/SelfServiceRegistration/components/PaymentProcessingLoader.vue';
import Modal from '@/modules/SelfServiceRegistration/components/Modal.vue';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import PaymentForm from '@/modules/SelfServiceRegistration/components/PaymentForm.vue';
import CoreSubscriptionShoppingCart from '@/modules/SelfServiceRegistration/components/ShoppingCart/CoreSubscriptionShoppingCart.vue';
import LayoutApplicationStyle from '@/modules/SelfServiceRegistration/components/LayoutApplicationStyle.vue';
import BaseCard from '@/components/BaseCard/BaseCard.vue';
import usePricePlans, { PlanProductKey } from '@/hooks/usePricePlans';
import useTrials, { trialPrice } from '@/hooks/useTrials';
import useRecurlyConvert from '@/modules/SelfServiceRegistration/hooks/useRecurlyConvert';
import { bandPlanFor } from '@/modules/SelfServiceRegistration/hooks/useSupervisionPlans';
import { fetchRecurlySellable } from '@/services/PricePlans';
import Analytics from '@/services/Analytics';
import { celebrate } from '@/plugins/vueConfetti';
import { error as DDError } from '@/services/Datadog';
import TrialTimer from '@/modules/SelfServiceRegistration/components/TrialTimer/TrialTimer.vue';
import { netlifyEnterTest } from '@/utils/netlifyTest';
import { createMarketingDTO } from '@/services/Registration';
import { BillingPeriod } from '@/constants/Billing';

// The 2.0 pitch from the v3 payment design (BIO-2152).
const valueBullets = [
  {
    lead: 'Your whole company, covered.',
    body: 'Bambee watches your company, works directly with your people, and does more of what needs to happen, as much as you hand over.',
  },
  {
    lead: 'Compliant every day, as the rules around your business change.',
    body: 'Gaps found, fixed, and verified. Nothing piles up, and no one who comes checking catches you off guard.',
  },
  {
    lead: 'Every issue gets an owner, a process, and an outcome.',
    body: 'The complaint nobody wrote down, the termination that keeps getting postponed: with Bambee, nothing slips through the cracks.',
  },
  {
    lead: 'Your Bambee team, from day one.',
    body: 'An HR Coverage Advisor, HR Manager, Chief of Staff, and Staff Coordinator who watch your company, coach your managers, and run the follow-through.',
  },
];

export default {
  name: 'PaymentPage',
  components: {
    BaseCard,
    BaseDivider,
    BaseIcon,
    LayoutApplicationStyle,
    Modal,
    PaymentForm,
    PaymentProcessingLoader,
    ProgressStepper,
    CoreSubscriptionShoppingCart,
    TypeBody,
    TypeDisplay,
    TypeHeading,
    TrialTimer,
  },

  setup(_, { emit, root }) {
    /**
     * HOOKS
     */
    const { state: registrationState, updateRegistration } = useRegistration();
    const { setSelectedPlanById, setWaivedSetupFee, getAppropriatePlanByTier } =
      usePricePlans();
    const { trialDuration } = useTrials();

    /**
     * The plan convert bills (BIO-1872): Standard Supervision at the company's band (BIO-2201),
     * from the Recurly sellable list, so the value is one convert accepts. Provisional: the
     * customer picks the real plan after payment (BIO-2154) and can change it later. The band
     * follows bandPlanFor, the rule the plan screen uses.
     */
    const checkoutPlan = ref<any>(null);
    fetchRecurlySellable([PlanProductKey.STANDARD_SUPERVISION])
      .then((plans) => {
        // The same band the plan screen prices (bandPlanFor), so a lead never converts on one
        // band and is shown another.
        checkoutPlan.value =
          bandPlanFor(
            plans.filter((p) => p.billingPeriod === BillingPeriod.MONTH),
            String(
              registrationState[RegistrationStateNames.NUM_EMPLOYEES] || ''
            )
          ) || null;
      })
      .catch((err) => DDError('Could not load the Recurly plan list', err));

    const {
      convert,
      error: errorMessage,
      isConverting: isProcessingPayment,
    } = useRecurlyConvert();
    const isPaymentSuccessful = ref(false);
    const isAnimatingPaymentProcessing = ref(false);

    onMounted(() => {
      netlifyEnterTest();
    });

    const btnTitle = computed(() => {
      if (isPaymentSuccessful.value) {
        return 'Payment Successful';
      }

      return `Start ${trialDuration.value} Day Trial`;
    });

    const availablePlanByTier = computed(() => {
      // @ts-ignore
      return getAppropriatePlanByTier(
        (registrationState[RegistrationStateNames.TIER_NUMBER] || 1) as number
      );
    });

    // The steps after payment: plan choice, then installation (BIO-2149). The design's
    // "Meet Your Team" has no step behind it.
    const nextSteps = ['Start Trial', 'Choose Your Plan', 'Set Up Bambee'];

    const companyId = computed(
      () => registrationState[RegistrationStateNames.COMPANY_ID]
    );

    const isReady = computed(() => {
      return checkoutPlan.value && companyId.value;
    });

    const showModal = computed(() => {
      return (
        isProcessingPayment.value ||
        (isPaymentSuccessful.value && !isAnimatingPaymentProcessing.value)
      );
    });

    /**
     * Methods
     */

    const handleSubmitPay = async ({ tokenId, method, cardHolderName }) => {
      isAnimatingPaymentProcessing.value = true;
      isPaymentSuccessful.value = false;

      // A trial carries no setup fee: convert rejects one above the plan's own, and the
      // idempotency key covers token, plan, fee and trial.
      const payload = {
        recurlyTokenId: tokenId,
        plan: checkoutPlan.value.value,
        setupFee: 0,
        trial: trialDuration.value,
        originUrl: window.location.href,
        marketing: createMarketingDTO(registrationState),
      };
      const amountPaidToday = trialPrice;

      // Null on a decline, 400/409 or requiresAction: the hook holds the message and the
      // form is editable again.
      const res = await convert(String(companyId.value), payload, method);
      if (!res) {
        return;
      }

      const products = [
        {
          type: checkoutPlan.value.name,
          plan: payload.plan,
          quantity: 1,
          amount: checkoutPlan.value.price,
        },
        {
          type: 'Setup Fee',
          plan: 'Setup Fee',
          quantity: 1,
          amount: payload.setupFee,
        },
      ];

      const timestamp = Date.now();
      // Random unique id to associate to the lead for Facebook tracking
      const leadID = Math.floor(timestamp * Math.random() * 10000000);

      Analytics.track(
        'lead_converted_origin',
        {
          companyId: res._id,
          products: unescape(JSON.stringify(products)),
          amount: amountPaidToday,
          fee: payload.setupFee,
          originalFee: checkoutPlan.value.setupFee,
          type: 'self-signup',
          owner: res._owner._id,
          origin: 'GET/WWW',
          currency: 'USD',
          leadID,
          ...res,
        },
        {
          Salesforce: true,
        }
      );

      Analytics.track('acquired-credit-card', {
        ...payload,
        recurlyTokenId: null,
        method,
        companyId: res._id,
        products: unescape(JSON.stringify(products)),
        amount: amountPaidToday,
        originalFee: checkoutPlan.value.setupFee,
        type: 'self-signup',
        owner: res._owner._id,
        origin: 'GET/WWW',
        tierNumber: checkoutPlan.value.tierNumber,
        category: 'get',
        label: 'try-bambee-payment',
        name: cardHolderName,
        marketing: createMarketingDTO(registrationState),
        user: res.data, // There is no res.data, this is a bug
      });

      // Google tag specifically for trial conversions/signups
      // @ts-ignore
      if (window.gtag && trialDuration.value > 0) {
        // @ts-ignore
        window.gtag('event', 'conversion', {
          send_to: 'AW-799566356/YPazCPLn3ZwZEJTUof0C',
        });
      }

      isPaymentSuccessful.value = true;
      // @ts-ignore
      celebrate(root.$confetti);

      emit('submit', {
        [RegistrationStateNames.COMPANY_CONVERTED_AT]: res.converted_at,
        [RegistrationStateNames.PURCHASED_PLAN_ID]: res.plan,
      });
    };

    const handleAnimationFinished = () => {
      isAnimatingPaymentProcessing.value = true;
    };

    /**
     * Watchers/Executions
     */

    // The Stripe-list plan for the tier still lands in registration state (PLAN_ID), which
    // the steps after payment read; it is not what convert is sent.
    setWaivedSetupFee(true);

    let unwatch = () => {};
    unwatch = watch(
      availablePlanByTier,
      (plan) => {
        if (plan) {
          setSelectedPlanById(plan.value);
          updateRegistration({
            [RegistrationStateNames.PLAN_ID]: plan.value,
          });

          unwatch();
        }
      },
      {
        immediate: true,
      }
    );

    return {
      btnTitle,
      checkoutPlan,
      errorMessage,
      handleAnimationFinished,
      handleSubmitPay,
      isPaymentSuccessful,
      isProcessingPayment,
      isReady,
      nextSteps,
      showModal,
      trialDuration,
      trialPrice,
      valueBullets,
    };
  },
};
